import {
  DataProviderQueryStatus,
  type DataProviderResult,
  type TableData,
} from '../DataProvider/types';
import {
  convertTableCurrency,
  selectEntityRows,
  selectTableMetrics,
} from '../DataProvider/utils/table-data';
import type { DataResolverPlan, PlannedItem } from './plan-data-queries';

export type ResolvedData = DataProviderResult<TableData>;
export type QueryTables = Readonly<Record<string, TableData>>;
export type QueryResult = DataProviderResult<QueryTables>;
export type QueryResults = Readonly<Record<string, QueryResult | null>>;
export type ResolvedItems = Readonly<Record<string, ResolvedData>>;

const PENDING: ResolvedData = {
  data: undefined,
  status: DataProviderQueryStatus.Idle,
};

const itemData = (
  { tableKey, metrics, entityId }: PlannedItem,
  result: QueryResult | undefined,
  exchangeRate: number
): ResolvedData => {
  if (result === undefined) return PENDING;
  const shared = result.data?.[tableKey];
  const table =
    shared && entityId !== undefined
      ? selectEntityRows(shared, entityId)
      : shared;
  if (
    result.data &&
    !table &&
    result.status !== DataProviderQueryStatus.Error
  ) {
    return {
      data: undefined,
      status: DataProviderQueryStatus.Fetching,
    };
  }
  return {
    ...result,
    data:
      table &&
      convertTableCurrency(selectTableMetrics(table, metrics), exchangeRate),
  };
};

export const resolveItems = (
  plan: DataResolverPlan,
  results: QueryResults,
  exchangeRate: number
): ResolvedItems =>
  Object.entries(plan.items).reduce<Record<string, ResolvedData>>(
    (items, [itemId, planned]) => {
      const result = results[planned.queryKey];
      if (result !== null) {
        items[itemId] = itemData(planned, result, exchangeRate);
      }
      return items;
    },
    {}
  );

export const isSameResult = (
  previous: QueryResult | null | undefined,
  next: QueryResult | null | undefined
): boolean => {
  if (!previous || !next) return previous === next;
  return (
    previous.data === next.data &&
    previous.status === next.status &&
    previous.error === next.error
  );
};
